// MoveSelectionDialog.js
//
// Precise numeric nudge for the current selection. Opened with ⌘⇧M (mac) /
// Ctrl+Shift+M (windows) while in Select mode with something selected.
// (Shift is in the chord because plain ⌘M is macOS "Minimize window".)
//
// Why this exists:
//   Arrow-key nudge (LineManager.handleEditLine) moves in fixed 2-px steps
//   — 20 px with Shift held. In image pixels, at a typical GSD of ~1 cm/px
//   that's a 2 cm / 20 cm jump with no way to land on an exact offset. Users
//   laying out rows need "move this 6 inches east", not "tap right 8 times
//   and hope". This dialog converts a real-world distance to pixels via GSD
//   and applies it once.
//
// Sign convention (spelled out in the UI so it can't be guessed wrong):
//   +X = right / east, +Y = up / north.
// Note the Y flip — image pixel coordinates grow DOWNWARD, so the parent
// negates dy when it converts to pixels. Both fields default to 0 and to
// the positive direction; the user types a negative number to go the other
// way.
//
// Positioned to match RepeatLineDialog (top-right rail) so it never covers
// the selection the user is about to move.

import React, { useEffect, useRef, useState } from 'react';

// Centimeters per unit — GSD is cm/px, so px = (value * CM_PER_UNIT) / gsd.
const CM_PER_UNIT = {
  mm: 0.1,
  in: 2.54,
};

const UNIT_OPTIONS = [
  { value: 'mm', label: 'mm' },
  { value: 'in', label: 'in' },
];

const labelStyle = {
  fontSize: '0.78rem',
  color: '#334',
  whiteSpace: 'nowrap',
};

const inputStyle = {
  fontSize: '0.82rem',
  width: '5.5rem',
  padding: '0.25rem 0.4rem',
  borderRadius: 4,
  border: '1px solid #ccc',
  textAlign: 'right',
};

const MoveSelectionDialog = ({ isOpen, onClose, onConfirm, gsd, selectionLabel }) => {
  const [x, setX] = useState('0');
  const [y, setY] = useState('0');
  // Unit persists across opens (a user working in inches stays in inches);
  // the amounts do not — every open starts from 0 so a stale offset can
  // never be applied by reflex.
  const [unit, setUnit] = useState('in');
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setX('0');
    setY('0');
    // Focus + select the X field so the user can type over the 0 immediately.
    const id = setTimeout(() => {
      if (firstFieldRef.current) {
        firstFieldRef.current.focus();
        firstFieldRef.current.select();
      }
    }, 0);
    return () => clearTimeout(id);
  }, [isOpen]);

  // Escape closes. Registered on the window (not the dialog) so it works
  // regardless of where focus landed. Capture phase keeps ImageEditor's
  // global Escape handler from clearing the selection out from under a
  // dialog the user is only trying to dismiss.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const gsdValid = typeof gsd === 'number' && isFinite(gsd) && gsd > 0;
  const parsedX = parseFloat(x);
  const parsedY = parseFloat(y);
  const xNum = isFinite(parsedX) ? parsedX : 0;
  const yNum = isFinite(parsedY) ? parsedY : 0;

  // Preview the pixel delta so the user can sanity-check the GSD conversion
  // before committing — a wrong GSD is otherwise invisible until after the move.
  const pxPerUnit = gsdValid ? CM_PER_UNIT[unit] / gsd : 0;
  const dxPx = xNum * pxPerUnit;
  const dyPx = -yNum * pxPerUnit; // screen/image Y grows downward

  const canConfirm = gsdValid && (xNum !== 0 || yNum !== 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canConfirm) return;
    onConfirm({ dxPx, dyPx, x: xNum, y: yNum, unit });
  };

  return (
    <div
      role="dialog"
      aria-label="Move selection"
      className="modal-content"
      style={{
        position: 'fixed',
        top: '68px',
        right: '12px',
        width: '16rem',
        backgroundColor: 'white',
        padding: '0.75rem 0.9rem',
        borderRadius: '10px',
        border: '1px solid #cfd7d0',
        boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
        zIndex: 1150,
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.15rem' }}>
        <h2 style={{ fontSize: '0.95rem', margin: 0, fontWeight: 700 }}>Move selection</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: '2px 6px', fontSize: '1rem', color: '#556', lineHeight: 1,
          }}
        >×</button>
      </div>
      {selectionLabel && (
        <p style={{ fontSize: '0.7rem', color: '#667', margin: '0 0 0.55rem 0' }}>
          Moving: <strong>{selectionLabel}</strong>
        </p>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.4rem' }}>
          <label htmlFor="move-x" style={labelStyle}>X <span style={{ color: '#889' }}>(+ right)</span></label>
          <input
            type="number"
            id="move-x"
            ref={firstFieldRef}
            value={x}
            onChange={(e) => setX(e.target.value)}
            step="any"
            style={inputStyle}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.4rem' }}>
          <label htmlFor="move-y" style={labelStyle}>Y <span style={{ color: '#889' }}>(+ up)</span></label>
          <input
            type="number"
            id="move-y"
            value={y}
            onChange={(e) => setY(e.target.value)}
            step="any"
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={labelStyle}>Units</span>
          <div role="radiogroup" aria-label="Move units" style={{ display: 'inline-flex', gap: '0.25rem' }}>
            {UNIT_OPTIONS.map(({ value, label }) => {
              const active = unit === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setUnit(value)}
                  style={{
                    width: '2.6rem', height: '1.6rem',
                    padding: 0, borderRadius: 4,
                    border: active ? '2px solid #2e7d32' : '1px solid #ccc',
                    backgroundColor: active ? '#e8f5e9' : 'white',
                    color: active ? '#2e7d32' : '#334',
                    fontFamily: 'inherit',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >{label}</button>
              );
            })}
          </div>
        </div>

        {/* Live conversion readout — the only place the user can catch a bad
            GSD before the geometry moves. */}
        <p style={{ fontSize: '0.68rem', color: gsdValid ? '#667' : '#a02522', margin: '0 0 0.6rem 0', lineHeight: 1.35 }}>
          {gsdValid
            ? <>= {dxPx.toFixed(1)} px X, {dyPx.toFixed(1)} px Y <span style={{ color: '#889' }}>(GSD {gsd.toFixed(5)} cm/px)</span></>
            : <>GSD unavailable — cannot convert {unit} to pixels. Load an image with geo metadata first.</>}
        </p>

        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button type="submit" disabled={!canConfirm} style={{
              flex: 1,
              padding: '0.4rem 0.6rem', fontSize: '0.82rem',
              backgroundColor: canConfirm ? '#2e7d32' : '#c3cdc4',
              color: 'white', border: 'none', borderRadius: 5,
              cursor: canConfirm ? 'pointer' : 'not-allowed', fontWeight: 600,
          }}>Confirm</button>
          <button type="button" onClick={onClose} style={{
              flex: 1,
              padding: '0.4rem 0.6rem', fontSize: '0.82rem', backgroundColor: '#eef2ee',
              color: '#334', border: '1px solid #cfd7d0', borderRadius: 5, cursor: 'pointer', fontWeight: 600,
          }}>Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default MoveSelectionDialog;
